import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Home,
  Phone,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Search,
  Filter,
  Wrench,
  AlertTriangle,
  Copy,
  ExternalLink,
  IndianRupee,
  LogOut,
  Send
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { WhatsAppButton } from './ui/whatsapp-button';
import { WHATSAPP_TEMPLATES } from '../lib/whatsapp';

const AdminServiceRequests = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [requests, setRequests] = useState([]);
  const [providers, setProviders] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, quoted: 0, accepted: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);

  useEffect(() => {    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [requestsRes, providersRes, statsRes] = await Promise.all([
        api.get(`/service-requests`),
        api.get(`/service-providers`),
        api.get(`/service-requests/stats/summary`)
      ]);
      setRequests(requestsRes.data.items || []);
      setProviders(providersRes.data.items || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async (requestId) => {
    setQuotesLoading(true);
    try {
      const res = await api.get(`/quotes/request/${requestId}`);
      setQuotes(res.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  };

  const openDetail = async (req) => {
    setSelectedRequest(req);
    await fetchQuotes(req.id);
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await api.patch(`/service-requests/${requestId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const copyQuoteLink = (requestId, providerId) => {
    const link = `${window.location.origin}/submit-quote/${requestId}/${providerId}`;
    navigator.clipboard.writeText(link);
    toast.success('Quote link copied! Share with the provider.');
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      quoted: { color: 'bg-blue-100 text-blue-800', label: 'Quoted' },
      accepted: { color: 'bg-green-100 text-green-800', label: 'Accepted' },
      completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const c = config[status] || config.pending;
    return <Badge className={c.color} data-testid={`status-${status}`}>{c.label}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    const config = {
      urgent: { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'Urgent' },
      normal: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Normal' },
      can_wait: { color: 'bg-green-100 text-green-800', icon: Clock, label: 'Can Wait' }
    };
    const c = config[urgency] || config.normal;
    const Icon = c.icon;
    return (
      <Badge className={c.color}>
        <Icon className="h-3 w-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const filteredRequests = requests.filter(r => {
    const matchesSearch =
      r.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.customerPhone.includes(searchTerm) ||
      r.problemTitle.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const relevantProviders = selectedRequest
    ? providers.filter(p => p.category === selectedRequest.category && p.active)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-service-requests-page">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Service Requests</h1>
                <p className="text-sm text-gray-600">Manage customer service requests & quotes</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/admin/dashboard')} variant="outline">
                <Home className="h-4 w-4 mr-2" /> Dashboard
              </Button>
              <Button onClick={() => navigate('/admin/service-providers')} variant="outline">
                <Wrench className="h-4 w-4 mr-2" /> Providers
              </Button>
              <Button onClick={handleLogout} variant="outline" className="text-red-600 hover:bg-red-50">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-900' },
            { label: 'Pending', value: stats.pending, color: 'text-yellow-600' },
            { label: 'Quoted', value: stats.quoted, color: 'text-blue-600' },
            { label: 'Accepted', value: stats.accepted, color: 'text-green-600' },
            { label: 'Completed', value: stats.completed, color: 'text-gray-600' }
          ].map((s) => (
            <Card key={s.label} data-testid={`stat-${s.label.toLowerCase()}`}>
              <CardContent className="p-4 text-center">
                <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-sm text-gray-600">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, phone, or problem..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-44" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-44" data-testid="category-filter">
                  <Wrench className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Plumber">Plumber</SelectItem>
                  <SelectItem value="Electrician">Electrician</SelectItem>
                  <SelectItem value="Painter">Painter</SelectItem>
                  <SelectItem value="Carpenter">Carpenter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        <Card>
          <CardHeader>
            <CardTitle>Service Requests ({filteredRequests.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12" data-testid="empty-state">
                <Wrench className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No service requests found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRequests.map((req) => (
                  <Card key={req.id} className="hover:shadow-md transition-shadow" data-testid={`request-card-${req.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">{req.problemTitle}</h3>
                            {getStatusBadge(req.status)}
                            {getUrgencyBadge(req.urgency)}
                            <Badge variant="outline">{req.category}</Badge>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <Phone className="h-3.5 w-3.5 text-blue-600" />
                              <span>{req.customerName} - {req.customerPhone}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 text-blue-600" />
                              <span className="truncate">{req.location}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 text-blue-600" />
                              <span>{formatDate(req.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button variant="outline" size="sm" onClick={() => openDetail(req)} data-testid={`view-btn-${req.id}`}>
                            <Eye className="h-4 w-4 mr-1" /> View
                          </Button>
                          {req.status === 'accepted' && (
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateStatus(req.id, 'completed')} data-testid={`complete-btn-${req.id}`}>
                              <CheckCircle className="h-4 w-4 mr-1" /> Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedRequest(null); setQuotes([]); }} data-testid="request-detail-modal">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-blue-600" />
                  Request Details
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => { setSelectedRequest(null); setQuotes([]); }} data-testid="close-modal-btn">
                  <XCircle className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Status & Badges */}
              <div className="flex items-center gap-3 flex-wrap">
                {getStatusBadge(selectedRequest.status)}
                {getUrgencyBadge(selectedRequest.urgency)}
                <Badge variant="outline">{selectedRequest.category}</Badge>
              </div>

              {/* Problem Info */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedRequest.problemTitle}</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedRequest.problemDescription}</p>
              </div>

              {/* Images */}
              {selectedRequest.images && selectedRequest.images.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500 mb-2 block">Problem Images</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {selectedRequest.images.map((img, i) => (
                      <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                        <img src={img} alt={`Problem ${i + 1}`} className="w-full h-28 object-cover rounded-lg border hover:opacity-80" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Customer Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Customer Info</h4>
                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <span>{selectedRequest.customerName}</span> -
                    <a href={`tel:${selectedRequest.customerPhone}`} className="text-blue-600 hover:underline">{selectedRequest.customerPhone}</a>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    <span>{selectedRequest.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span>{formatDate(selectedRequest.createdAt)}</span>
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Update Status</h4>
                <div className="flex gap-2 flex-wrap">
                  {['pending', 'quoted', 'accepted', 'completed', 'cancelled'].map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={selectedRequest.status === s ? 'default' : 'outline'}
                      className={selectedRequest.status === s ? 'bg-blue-600' : ''}
                      onClick={() => updateStatus(selectedRequest.id, s)}
                      data-testid={`set-status-${s}`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Quotes Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Quotes ({quotes.length})</h4>
                {quotesLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : quotes.length === 0 ? (
                  <p className="text-sm text-gray-500">No quotes received yet.</p>
                ) : (
                  <div className="space-y-3">
                    {quotes.map((q) => (
                      <Card key={q.id} className={q.status === 'accepted' ? 'border-2 border-green-500' : ''} data-testid={`quote-${q.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-gray-900">{q.providerName}</span>
                                {q.status === 'accepted' && <Badge className="bg-green-100 text-green-800">Accepted</Badge>}
                                {q.status === 'rejected' && <Badge className="bg-gray-100 text-gray-600">Rejected</Badge>}
                                {q.status === 'pending' && <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" />{q.estimatedCost}</span>
                                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{q.estimatedTime}</span>
                                <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{q.providerPhone}</span>
                              </div>
                              <p className="text-sm text-gray-700 mt-1">{q.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Send to Providers */}
              {relevantProviders.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Send Quote Request to Providers</h4>
                  <p className="text-sm text-gray-600 mb-3">Share the quote link with {selectedRequest.category} providers:</p>
                  <div className="space-y-2">
                    {relevantProviders.map((p) => (
                      <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`provider-row-${p.id}`}>
                        <div className="flex items-center gap-3">
                          <Wrench className="h-5 w-5 text-blue-600" />
                          <div>
                            <span className="font-medium text-gray-900">{p.name}</span>
                            <span className="text-sm text-gray-500 ml-2">{p.phone}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => copyQuoteLink(selectedRequest.id, p.id)} data-testid={`copy-link-${p.id}`}>
                            <Copy className="h-3.5 w-3.5 mr-1" /> Copy Link
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => window.open(`${window.location.origin}/submit-quote/${selectedRequest.id}/${p.id}`, '_blank')} data-testid={`open-link-${p.id}`}>
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
                          </Button>
                          <WhatsAppButton
                            phone={p.phone}
                            message={WHATSAPP_TEMPLATES.providerQuoteRequest({
                              providerName: p.name,
                              customerName: selectedRequest.customerName,
                              category: selectedRequest.category,
                              problemTitle: selectedRequest.problemTitle,
                              url: `${window.location.origin}/submit-quote/${selectedRequest.id}/${p.id}`,
                            })}
                            data-testid={`whatsapp-link-${p.id}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {relevantProviders.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  No active {selectedRequest.category} providers found. <Button variant="link" className="text-amber-800 underline p-0 h-auto" onClick={() => navigate('/admin/service-providers')}>Add providers</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminServiceRequests;
